/**
 * Who is signed in, for diagnostics only — never for authorization.
 *
 * The `client-list-empty` reports need to name the affiliate account, because the
 * anomaly looks account-specific. Reading localStorage at report time is not
 * enough: when a session dies, the axios interceptor clears storage and redirects
 * *before* the rejection reaches the code that reports the anomaly, so the event
 * reaches Sentry with no user at all — the worst case for a per-account bug. Every
 * authenticated request records the identity here first, so a report that finds
 * storage already emptied can still fall back to the last known value.
 *
 * Remove together with the rest of the client-list investigation.
 */

export type AffiliateIdentity = { id?: string; email?: string };

/**
 * The affiliate login response stored in localStorage carries no top-level
 * `email` — it only exists as a claim inside the Cognito id token. Decode the JWT
 * payload (base64url, no verification: this is display data, not a trust
 * decision) as a fallback.
 */
const emailFromToken = (token?: string): string | undefined => {
  try {
    if (!token) return undefined;
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof payload.email === "string" ? payload.email : undefined;
  } catch {
    return undefined;
  }
};

export const identityFromUser = (usr: any): AffiliateIdentity => ({
  id: usr?.pnoe_user_id,
  email: usr?.email ?? emailFromToken(usr?.token),
});

let lastKnown: AffiliateIdentity | null = null;

/** Records an identity if it carries anything usable. Never throws. */
export const rememberIdentity = (usr: any): void => {
  try {
    const identity = identityFromUser(usr);
    if (!identity.id && !identity.email) return;
    lastKnown = identity;
  } catch {
    /* diagnostics must never break a request */
  }
};

export const getLastKnownIdentity = (): AffiliateIdentity | null => lastKnown;
